import {skipToken} from '@reduxjs/toolkit/query';

import {useGetVehicleQuery} from '@dms/api/sales';
import {useGetMetadaServiceCaseQuery} from '@dms/api/workshop';
import {levelIndicatorsFor} from '@dms/shared';

import {isNotNilOrEmpty} from 'shared/web';

/**
 * Which level gauges a service case's vehicle has, for call sites that only know the service
 * case. Both queries are already in flight on the service case detail page, so this resolves
 * from the cache. `isLoading` matters: until the vehicle resolves the fuel type is unknown and
 * `levelIndicatorsFor` falls back to fuel, which would briefly show a combustion gauge on an
 * electric vehicle — gate the form on it. `isError` matters for the same reason: a failed vehicle
 * request leaves the fuel type unknowable, not combustion.
 */
export function useVehicleLevelIndicators(serviceCaseId: string) {
  const {
    data: serviceCase,
    isLoading: isServiceCaseLoading,
    isError: isServiceCaseError,
  } = useGetMetadaServiceCaseQuery(isNotNilOrEmpty(serviceCaseId) ? {serviceCaseId} : skipToken);

  const vehicleId = serviceCase?.vehicleId;

  const {
    data: vehicle,
    isLoading: isVehicleLoading,
    isError: isVehicleError,
  } = useGetVehicleQuery(isNotNilOrEmpty(vehicleId) ? {vehicleId} : skipToken);

  const levelIndicators = levelIndicatorsFor(vehicle?.fuelType);

  return {
    levelIndicators,
    isLoading: isServiceCaseLoading || isVehicleLoading,
    isError: isServiceCaseError || isVehicleError,
  };
}
